import axios from 'axios';
import { authService } from '@features/auth/services/authService';

export interface StopEditInput {
  stopId: number;
  /** The stop's service order (vehicle fields live there). */
  serviceOrderId: number | null;
  /** service_stop_details fields (customer_name, customer_phone, service_description) */
  detail?: Record<string, unknown>;
  /** mobility_stops fields (address + resolved lat/lng/location_id) */
  stop?: Record<string, unknown>;
  /** service_orders fields (vehicle_year, vehicle_make, vehicle_model) */
  vehicle?: Record<string, unknown>;
}

export interface RunEditPayload {
  runId: number;
  /** Final stop order — every remaining stop id, in the desired sequence. */
  orderedStopIds: number[];
  /** Stops removed from the run (their appointments return to the queue). */
  removedStopIds: number[];
  /** Per-stop field edits (phase 2 — inline stop editing). */
  stopEdits?: StopEditInput[];
  /**
   * Appointments to add as new stops (phase 3). `position` is the final index
   * in the combined stop order; the server splices them in there.
   * `serviceWindow` is the customer-facing formatted appointment time,
   * persisted to the new stop's service_stop_details.service_window.
   */
  addedStops?: { appointmentId: number; position: number; serviceWindow?: string | null }[];
}

export interface RunEditResult {
  success: boolean;
  removedStops?: number;
  reordered?: boolean;
  editedStops?: number;
  addedStops?: number;
  requeuedAppointments?: number;
  error?: string;
}

/**
 * Apply a run-edit draft (reorder + removals) as one reviewed transaction.
 * The server re-validates against current state — a stop whose status
 * advanced past the locking rules since the draft was taken rejects the
 * whole edit; surface the error and let the coordinator re-review.
 */
export async function applyRunEdit(payload: RunEditPayload): Promise<RunEditResult> {
  const token = await authService.getAccessTokenWithAutoRefresh();
  if (!token) {
    return { success: false, error: 'You must be signed in to edit a run' };
  }

  try {
    const { data } = await axios.post('/.netlify/functions/editMobileServiceRun', payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return data as RunEditResult;
  } catch (err: any) {
    const serverError = err?.response?.data?.error;
    return { success: false, error: serverError || err?.message || 'Failed to apply changes' };
  }
}
